import React from 'react'
import { Button, DesignedModal, Tooltip } from '@clickcease/cc-client-components-lib'
import { Trans, useTranslation } from 'react-i18next'
import {
  DetailsBox,
  BoxRow,
  BoxRowTitle,
  BoxRowItem,
  DiscountText,
  DividerLine,
  SupportText,
  StyledLink,
} from './ConfirmPlanModal.styled'
import { commaSeparatedNumber } from 'Utils/number'
import { useZendeskChat } from 'Hooks/zendesk/useZendeskChat'
import { useNavigate } from 'react-router-dom'
import { useGetAccountMetadata } from 'Features/ClickceaseMigrationBanner/api/useGetAccountMetadata'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { CURRENCIES } from 'Types/Currencies'
import { getAmountWithCurrency } from 'Utils/currencyHelper'

const MONTHS = 12
const NS = 'limitedOfferBanner'

type ConfirmPlanModalProps = {
  isOpen: boolean
  loading: boolean
  setIsOpen: (isOpen: boolean) => void
  onConfirm: () => void
}

export const ConfirmPlanModal = ({
  isOpen,
  loading,
  setIsOpen,
  onConfirm,
}: ConfirmPlanModalProps) => {
  const { t } = useTranslation(NS)
  const { data: accountMetadata } = useGetAccountMetadata()
  const { data: currentPlanDetails } = useGetPlansDetails()
  const { openChat } = useZendeskChat()
  const navigate = useNavigate()

  const percentage = `${accountMetadata?.essentialsDiscountPercentage || 0}%`

  const currency = currentPlanDetails?.plans[0].currency || CURRENCIES.USD

  const totalPrice = Math.ceil(accountMetadata?.essentialsDiscountPrice || 0)

  const discountedPrice = accountMetadata
    ? Math.floor(accountMetadata.essentialsProductPrice - accountMetadata.essentialsDiscountPrice)
    : 0

  const amount = (price?: number) => getAmountWithCurrency(currency, price)

  return (
    <DesignedModal
      title={t('modal.title')}
      subTitle={t('modal.sub_title')}
      onOpenChange={setIsOpen}
      isOpen={isOpen}
      style={{ width: '572px' }}
      actions={
        <>
          <Button variant="ghost" text={t('modal.btn_cancel')} onClick={() => setIsOpen(false)} />
          <Button text={t('modal.btn_confirm')} onClick={onConfirm} loading={loading} />
        </>
      }
    >
      <DetailsBox>
        <BoxRow>
          <BoxRowTitle>{t('modal.box_title')}</BoxRowTitle>
        </BoxRow>

        {currentPlanDetails &&
          currentPlanDetails.plans?.map((plan) => (
            <BoxRow key={plan.id}>
              <BoxRowItem>
                {t(`modal.${plan.product.toLowerCase()}_plan`, {
                  type: plan.type,
                })}
                <BoxRowItem block small>
                  {t(`modal.plan_limit`, {
                    limit: commaSeparatedNumber(plan.limit),
                  })}
                </BoxRowItem>
              </BoxRowItem>
              <BoxRowItem alignTop>{amount(plan.amount)}</BoxRowItem>
            </BoxRow>
          ))}

        <BoxRow>
          <DiscountText>
            {t('modal.discount_text', { discount: percentage })}{' '}
            <Tooltip tooltiptContent={t('modal.discount_text_tooltip')} />
          </DiscountText>
          <BoxRowItem green>-{amount(discountedPrice)}</BoxRowItem>
        </BoxRow>

        <DividerLine />

        <BoxRow>
          <BoxRowItem bold>{t('modal.total_pricing')}</BoxRowItem>
          <BoxRowItem>
            ({amount(Math.ceil(totalPrice / MONTHS))}/m){' '}
            <BoxRowItem bold>{amount(totalPrice)}</BoxRowItem>
          </BoxRowItem>
        </BoxRow>
      </DetailsBox>

      <SupportText first>
        <Trans ns={NS} i18nKey={'modal.replace_card'}>
          The card you used on ClickCease will be charged, here to
          <StyledLink onClick={() => navigate('account-settings')}>Replace card</StyledLink>
        </Trans>
      </SupportText>

      <SupportText>
        <Trans ns={NS} i18nKey={'modal.contact_support'}>
          Plan or billing questions? <StyledLink onClick={openChat}>Contact support</StyledLink>
        </Trans>
      </SupportText>
    </DesignedModal>
  )
}
